import React, { useCallback, useRef, useState } from "react";
import GroupChannel from "@sendbird/uikit-react/GroupChannel";
import { Sparkles, Lock } from "lucide-react";
import { useSendbird } from "@/hooks/useSendbird";
import { api } from "@/api/client";

/**
 * SendbirdEmbeddedChannel — thin wrapper around Sendbird UIKit's <GroupChannel>
 * that:
 *   1. Guards on Sendbird readiness (renders a lightweight skeleton until connected)
 *   2. When `customType === "ai_assistant"`, intercepts message-send and fires
 *      the backend Gemini reply endpoint. Same pattern as ChatSendbird page.
 *   3. Renders a "PILLS AI Assistant is typing" overlay while the AI reply is
 *      in flight.
 *
 * Props:
 *   - channelUrl (string)         : Sendbird channel URL to embed
 *   - customType (string?)        : If "ai_assistant", enables the Gemini intercept
 *   - height (string)             : CSS height for the container (default 600px)
 *   - onBeforeSendUserMessage     : optional external hook to chain
 *   - testId (string)             : data-testid for the outer container
 *   - emptyMessage (string)       : text shown when channelUrl is falsy
 */
const SendbirdEmbeddedChannel = ({
    channelUrl,
    customType,
    height = "600px",
    onBeforeSendUserMessage: chainedOnBefore,
    testId,
    emptyMessage = "This chat isn't available yet. Please try again shortly.",
}) => {
    const { ready } = useSendbird();
    const [aiThinking, setAiThinking] = useState(false);
    const aiInFlightRef = useRef(0);

    const triggerAiReply = useCallback(async (userMessage) => {
        aiInFlightRef.current += 1;
        setAiThinking(true);
        try {
            await api.post("/sendbird/ai/reply", { user_message: userMessage });
        } catch (err) {
            console.warn("[sendbird embed] AI reply failed:", err?.response?.data || err?.message);
        } finally {
            aiInFlightRef.current -= 1;
            if (aiInFlightRef.current <= 0) {
                aiInFlightRef.current = 0;
                setAiThinking(false);
            }
        }
    }, []);

    const onBeforeSendUserMessage = useCallback((params) => {
        if (customType === "ai_assistant") {
            const text = params?.message || "";
            if (text.trim()) triggerAiReply(text);
        }
        return chainedOnBefore ? chainedOnBefore(params) : params;
    }, [customType, triggerAiReply, chainedOnBefore]);

    if (!channelUrl) {
        return (
            <div
                className="grid place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-white/[0.02]"
                style={{ height }}
                data-testid={testId ? `${testId}-empty` : undefined}
            >
                <div>
                    <Lock className="mx-auto mb-2 h-5 w-5 text-slate-400" />
                    {emptyMessage}
                </div>
            </div>
        );
    }

    if (!ready) {
        return (
            <div
                className="grid place-items-center rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-white/[0.03]"
                style={{ height }}
                data-testid={testId ? `${testId}-loading` : undefined}
            >
                <div className="animate-pulse text-brand-500">Connecting to PILLS chat…</div>
            </div>
        );
    }

    return (
        <div
            className="relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-white/[0.03]"
            style={{ height }}
            data-testid={testId}
        >
            <GroupChannel
                channelUrl={channelUrl}
                onBeforeSendUserMessage={onBeforeSendUserMessage}
                onChatHeaderActionClick={() => {}}
            />
            {customType === "ai_assistant" && aiThinking && (
                <div
                    className="pointer-events-none absolute bottom-20 left-1/2 z-10 -translate-x-1/2 rounded-full border border-violet-200 bg-white/95 px-4 py-1.5 shadow-lg backdrop-blur dark:border-violet-500/30 dark:bg-zinc-900/95"
                    data-testid="ai-assistant-typing"
                >
                    <span className="flex items-center gap-2 text-xs font-medium text-violet-700 dark:text-violet-300">
                        <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                        PILLS AI Assistant is typing
                        <span className="inline-flex gap-0.5">
                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:0ms]"></span>
                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:120ms]"></span>
                            <span className="h-1 w-1 animate-bounce rounded-full bg-violet-500 [animation-delay:240ms]"></span>
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
};

export default SendbirdEmbeddedChannel;
